import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const InventoryContext = createContext();

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within InventoryProvider');
  }
  return context;
};

// Stock level thresholds
export const STOCK_LEVELS = {
  OUT_OF_STOCK: 0,
  LOW_STOCK: 10,
  MEDIUM_STOCK: 25,
  HIGH_STOCK: 50
};

export const InventoryProvider = ({ children }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsEnabled, setAlertsEnabled] = useState(true);

  // Subscribe to menu items for stock tracking
  useEffect(() => {
    const q = query(collection(db, 'menu_items'), orderBy('name'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMenuItems(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching inventory:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Calculate stock alerts whenever menu items change
  useEffect(() => {
    const alerts = menuItems
      .filter(item => {
        const stock = item.stockQuantity ?? null;
        if (stock === null) return false; // No stock tracking for this item
        return stock <= STOCK_LEVELS.LOW_STOCK;
      })
      .map(item => ({
        id: item.id,
        name: item.name,
        category: item.category,
        currentStock: item.stockQuantity || 0,
        level: item.stockQuantity === 0 ? 'out' : 'low',
        image: item.image,
        lastUpdated: item.stockUpdatedAt
      }))
      .sort((a, b) => a.currentStock - b.currentStock);

    setStockAlerts(alerts);
  }, [menuItems]);

  // Update stock quantity
  const updateStock = async (itemId, quantity) => {
    try {
      const itemRef = doc(db, 'menu_items', itemId);
      await updateDoc(itemRef, {
        stockQuantity: quantity,
        stockUpdatedAt: Timestamp.now(),
        isAvailable: quantity > 0
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating stock:', error);
      return { success: false, error };
    }
  };

  // Increment/Decrement stock
  const adjustStock = async (itemId, adjustment) => {
    const item = menuItems.find(i => i.id === itemId);
    if (!item) return { success: false, error: 'Item not found' };

    const currentStock = item.stockQuantity || 0;
    const newStock = Math.max(0, currentStock + adjustment);

    return updateStock(itemId, newStock);
  };

  // Enable stock tracking for an item
  const enableStockTracking = async (itemId, initialQuantity = 0) => {
    try {
      const itemRef = doc(db, 'menu_items', itemId);
      await updateDoc(itemRef, {
        stockTracking: true,
        stockQuantity: initialQuantity,
        stockUpdatedAt: Timestamp.now(),
        lowStockThreshold: STOCK_LEVELS.LOW_STOCK
      });
      return { success: true };
    } catch (error) {
      console.error('Error enabling stock tracking:', error);
      return { success: false, error };
    }
  };

  // Disable stock tracking for an item
  const disableStockTracking = async (itemId) => {
    try {
      const itemRef = doc(db, 'menu_items', itemId);
      await updateDoc(itemRef, {
        stockTracking: false,
        stockQuantity: null
      });
      return { success: true };
    } catch (error) {
      console.error('Error disabling stock tracking:', error);
      return { success: false, error };
    }
  };

  // Set custom low stock threshold
  const setLowStockThreshold = async (itemId, threshold) => {
    try {
      const itemRef = doc(db, 'menu_items', itemId);
      await updateDoc(itemRef, {
        lowStockThreshold: threshold
      });
      return { success: true };
    } catch (error) {
      console.error('Error setting threshold:', error);
      return { success: false, error };
    }
  };

  // Bulk update stock for multiple items
  const bulkUpdateStock = async (updates) => {
    try {
      const promises = updates.map(({ itemId, quantity }) =>
        updateDoc(doc(db, 'menu_items', itemId), {
          stockQuantity: quantity,
          stockUpdatedAt: Timestamp.now(),
          isAvailable: quantity > 0
        })
      );
      await Promise.all(promises);
      return { success: true };
    } catch (error) {
      console.error('Error bulk updating stock:', error);
      return { success: false, error };
    }
  };

  // Get items by stock level
  const getItemsByStockLevel = useCallback((level) => {
    return menuItems.filter(item => {
      const stock = item.stockQuantity;
      if (stock === null || stock === undefined) return false;

      switch (level) {
        case 'out':
          return stock === 0;
        case 'low':
          return stock > 0 && stock <= STOCK_LEVELS.LOW_STOCK;
        case 'medium':
          return stock > STOCK_LEVELS.LOW_STOCK && stock <= STOCK_LEVELS.MEDIUM_STOCK;
        case 'high':
          return stock > STOCK_LEVELS.MEDIUM_STOCK;
        default:
          return true;
      }
    });
  }, [menuItems]);

  // Statistics
  const stats = useMemo(() => {
    const trackedItems = menuItems.filter(item => item.stockTracking || item.stockQuantity !== undefined);
    const outOfStock = trackedItems.filter(item => (item.stockQuantity || 0) === 0);
    const lowStock = trackedItems.filter(item => {
      const stock = item.stockQuantity || 0;
      return stock > 0 && stock <= STOCK_LEVELS.LOW_STOCK;
    });
    const inStock = trackedItems.filter(item => (item.stockQuantity || 0) > STOCK_LEVELS.LOW_STOCK);

    return {
      totalItems: menuItems.length,
      trackedItems: trackedItems.length,
      outOfStock: outOfStock.length,
      lowStock: lowStock.length,
      inStock: inStock.length,
      alertCount: outOfStock.length + lowStock.length
    };
  }, [menuItems]);

  const value = {
    menuItems,
    stockAlerts,
    loading,
    alertsEnabled,
    setAlertsEnabled,
    stats,
    updateStock,
    adjustStock,
    enableStockTracking,
    disableStockTracking,
    setLowStockThreshold,
    bulkUpdateStock,
    getItemsByStockLevel,
    STOCK_LEVELS
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};
